/**
 * Staff Validation
 *
 * Client-side validation for staff member forms.
 * Reused by both StaffOnboardingPage and ManageStaffPage.
 */

import { normalizeIndianMobile } from './phone';
import { STAFF_ROLES } from '../constants/staff';

/**
 * Validate a single staff row.
 *
 * @param {Object} staff - Staff form data
 * @returns {Object} - Field-keyed error map (empty = valid)
 */
export function validateStaffRow(staff) {
  const errors = {};

  // first_name — required, 2-50 chars
  const firstName = (staff.first_name || '').trim();
  if (!firstName) {
    errors.first_name = 'First name is required';
  } else if (firstName.length < 2) {
    errors.first_name = 'At least 2 characters';
  } else if (firstName.length > 50) {
    errors.first_name = 'Max 50 characters';
  }

  // last_name — required, 2-50 chars
  const lastName = (staff.last_name || '').trim();
  if (!lastName) {
    errors.last_name = 'Last name is required';
  } else if (lastName.length < 2) {
    errors.last_name = 'At least 2 characters';
  } else if (lastName.length > 50) {
    errors.last_name = 'Max 50 characters';
  }

  // mobile — required, Indian format
  const mobile = (staff.mobile || '').trim();
  if (!mobile) {
    errors.mobile = 'Mobile number is required';
  } else {
    try {
      normalizeIndianMobile(mobile);
    } catch (e) {
      errors.mobile = e.message || 'Invalid mobile number';
    }
  }

  // email — optional, but if provided must be valid
  const email = (staff.email || '').trim();
  if (email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      errors.email = 'Invalid email address';
    }
  }

  // role — required
  if (!staff.role) {
    errors.role = 'Role is required';
  }

  // specialization — required when role is DOCTOR
  if (staff.role === STAFF_ROLES.DOCTOR && !(staff.specialization || '').trim()) {
    errors.specialization = staff.specialization_other
      ? 'Type the speciality, or pick one from the list.'
      : 'Specialization is required for doctors';
  }

  // role_name — required when role is OTHER_STAFF
  if (staff.role === 'OTHER_STAFF') {
    const roleName = (staff.role_name || '').trim();
    if (!roleName) {
      errors.role_name = 'Role name is required for Other Staff';
    } else if (roleName.length < 2) {
      errors.role_name = 'At least 2 characters';
    } else if (roleName.length > 50) {
      errors.role_name = 'Max 50 characters';
    }
  }

  return errors;
}

/**
 * Validate all staff rows. Returns array of error objects (one per row).
 * Also checks cross-row uniqueness for mobile and email.
 *
 * @param {Array<Object>} staffList
 * @returns {{ rowErrors: Object[], hasErrors: boolean }}
 */
export function validateStaffList(staffList) {
  const rowErrors = staffList.map((s) => validateStaffRow(s));

  // Cross-row duplicate mobile check
  const mobileSeen = {};
  const emailSeen = {};

  staffList.forEach((s, idx) => {
    const mobile = (s.mobile || '').trim();
    if (mobile && !rowErrors[idx].mobile) {
      try {
        const { e164 } = normalizeIndianMobile(mobile);
        if (mobileSeen[e164] !== undefined) {
          rowErrors[idx].mobile = 'Duplicate mobile number';
          rowErrors[mobileSeen[e164]].mobile = 'Duplicate mobile number';
        } else {
          mobileSeen[e164] = idx;
        }
      } catch {
        // already caught above
      }
    }

    const email = (s.email || '').trim().toLowerCase();
    if (email && !rowErrors[idx].email) {
      if (emailSeen[email] !== undefined) {
        rowErrors[idx].email = 'Duplicate email address';
        rowErrors[emailSeen[email]].email = 'Duplicate email address';
      } else {
        emailSeen[email] = idx;
      }
    }
  });

  const hasErrors = rowErrors.some((e) => Object.keys(e).length > 0);
  return { rowErrors, hasErrors };
}
